import { useEffect, useMemo, useState } from 'react'

import { Combobox } from '@headlessui/react'
import {
  InfiniteData,
  UseInfiniteQueryResult,
  UseQueryResult,
} from '@tanstack/react-query'
import { clsx } from 'clsx'
import { createSearchParams, Link as RouterLink } from 'react-router-dom'
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import useMobileDetect from 'use-mobile-detect-hook'

import { SystemSpaceSlug } from '@bettermode/common/space'
import { ClientError } from '@tribeplatform/gql-client/lib'
import { useSpaceUtil } from '@tribeplatform/react-sdk/hooks'
import { Hotkey, HOTKEY_MAPPING } from '@tribeplatform/react-ui-kit/Hotkey'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'
import { Link } from '@tribeplatform/react-ui-kit/Link'

import { T } from '../../../i18n/index.js'
import {
  LegacySearchEntity,
  LegacySearchQueryResult,
  ResultsView,
  SearchLoading,
  SMART_SEARCH_RESULTS_ALL_TAB_LIMIT,
  SMART_SEARCH_RESULTS_FETCH_MORE_PARAM,
  SmartSearchEntityWithUuid,
  SmartSearchResult,
  useSmartSearchUrlParams,
} from '../../index.js'
import {
  SmartSearchEntityType,
  SmartSearchPaginatedEntity,
} from '../../SmartSearch.types.js'

import { SearchEntityRow } from './SearchEntityRow.js'
import { SectionTitle } from './SectionTitle.js'

type SmartSearchResultsSectionProps = {
  type: SmartSearchEntityType
  queryResult:
    | UseInfiniteQueryResult<SmartSearchResult, ClientError>
    | UseQueryResult<SmartSearchResult, ClientError>
    | LegacySearchQueryResult<LegacySearchEntity>
  title?: string
  query: string
  hasTitle?: boolean
  view?: ResultsView
  titleClassName?: string
  onSwitchToSolely: (type: SmartSearchEntityType) => void
  spaceIds?: string[]
}

export const SmartSearchResultsSection = ({
  type,
  queryResult,
  title,
  query,
  hasTitle = true,
  view = 'command-bar',
  titleClassName,
  onSwitchToSolely,
  spaceIds = [],
}: SmartSearchResultsSectionProps) => {
  const { isMobile } = useMobileDetect()
  const { isLoading, data: queryData, isFetching } = queryResult // TODO: type the queryResult to make it easier to work
  const { generatePath } = useSpaceUtil()
  const { setParams, fetchMore, expanded, isInSearchPage } =
    useSmartSearchUrlParams()

  // Check if this section is the expanded one
  // This determines if the section should show more results and auto-fetch pagination
  const isExpandedSection = useMemo(() => {
    return expanded === type
  }, [expanded, type])

  const [isRenderingSolely, setIsRenderingSolely] = useState(
    isInSearchPage ? isExpandedSection : false,
  )

  useEffect(() => {
    if (isInSearchPage) {
      setIsRenderingSolely(isExpandedSection)
    }
  }, [isInSearchPage, isExpandedSection])

  // on the initial load, if the fetchMore param is present, fetch next page and remove the param
  // this is used to fetch more results when the user comes to the search results page from the popup clicking on the "More search results" link
  // this way, the user doesn't need to click on the link again to fetch more results
  useEffect(() => {
    if (!('fetchNextPage' in queryResult) || isLoading || isFetching) {
      return
    }

    if ((isInSearchPage && !fetchMore) || !isRenderingSolely) {
      return
    }

    setParams({ newFetchMore: null })

    queryResult.fetchNextPage()
  }, [
    fetchMore,
    isFetching,
    isInSearchPage,
    isLoading,
    isRenderingSolely,
    queryResult,
    setParams,
  ])

  // external search results are not paginated (is a simple array), others are paginated
  const data: SmartSearchEntityWithUuid[] = useMemo(() => {
    return Array.isArray(queryData)
      ? queryData.map(d => ({ ...d, uuid: crypto.randomUUID() }))
      : (queryData as InfiniteData<SmartSearchPaginatedEntity>)?.pages.flatMap(
          p => p.nodes.map(n => ({ ...n, uuid: crypto.randomUUID() })),
        )
  }, [queryData])

  const numberOfItemsToRender = SMART_SEARCH_RESULTS_ALL_TAB_LIMIT[type]

  const hasNextPage = Array.isArray(queryData)
    ? false // external search results are not paginated
    : (queryData as InfiniteData<SmartSearchPaginatedEntity>)?.pages.at(-1)
        .pageInfo.hasNextPage

  const truncatedResults = isRenderingSolely
    ? data
    : data?.slice(0, numberOfItemsToRender)

  const handleFetchMore = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault()

    if (isFetching) {
      return
    }

    if (isInSearchPage) {
      setIsRenderingSolely(true)
      setParams({ newType: type })
    }

    if ('fetchNextPage' in queryResult) {
      queryResult.fetchNextPage()
    }
  }

  const handleClickMoreResults = (e: React.MouseEvent<HTMLAnchorElement>) => {
    // if is mobile, then we don't want to navigate to the search results page
    if (isMobile()) {
      e.preventDefault()
      onSwitchToSolely(type)
      setIsRenderingSolely(true)
    }
  }

  // TODO: (mohsen) refactor the generatePath to support Array of strings as the value of query params
  const moreSearchResultsURL = useMemo(() => {
    const baseURL = generatePath(SystemSpaceSlug.SEARCH, null)

    // Use expanded parameter to indicate which section should be focused
    // Always use normalized type (posts for both official and regular posts)
    const normalizedType =
      type === SmartSearchEntityType.OFFICIAL_POSTS
        ? SmartSearchEntityType.POSTS
        : type

    const queryParams = createSearchParams({
      query,
      type: normalizedType,
      expanded: type, // The specific section that should be expanded
      [SMART_SEARCH_RESULTS_FETCH_MORE_PARAM]: 'true',
      spaceIds,
    })

    return `${baseURL}?${queryParams}`
  }, [generatePath, query, spaceIds, type])

  if (isLoading) {
    return <SearchLoading />
  }

  if (!data?.length) {
    return null
  }

  return (
    <div className={clsx('flex flex-col gap-2', { 'pt-3': !hasTitle })}>
      <div className="last:pb-1">
        {hasTitle && (
          <SectionTitle
            type={type}
            title={title}
            view={view}
            className={titleClassName}
          />
        )}

        <div className="px-2">
          {truncatedResults.map(item => (
            <SearchEntityRow key={item.uuid} item={item} type={type} />
          ))}
        </div>
      </div>

      {(data?.length > truncatedResults.length || hasNextPage) && (
        <div className="px-2 pb-1">
          <Combobox.Option
            disabled={isRenderingSolely && isFetching}
            value={{
              url: moreSearchResultsURL,
            }}
            className={({ active }) =>
              clsx(
                'flex items-center px-3 rounded-md select-none gap-4 py-2 w-full',
                {
                  'bg-surface-subdued cursor-pointer': active,
                },
              )
            }
          >
            {({ active }) => (
              <div className="flex items-center gap-1 w-full">
                <SvgIcon
                  className="w-6 h-6 mr-2.5 text-link"
                  name="dots-horizontal"
                />
                <Link
                  to={moreSearchResultsURL}
                  className={clsx('inline-flex items-center gap-1 flex-1', {
                    'animate-pulse': isFetching,
                    'min-h-12': type === SmartSearchEntityType.POSTS,
                  })}
                  disabled={isRenderingSolely && isFetching}
                  as={RouterLink}
                  onClick={
                    !!isInSearchPage || isRenderingSolely
                      ? handleFetchMore
                      : handleClickMoreResults
                  }
                >
                  <T
                    id="SmartSearch.MoreSearchResultsAbout"
                    defaultMessage='More search results about "{query}"'
                    values={{ query }}
                  />

                  <SvgIcon name="arrow-right" />
                </Link>
                {active && (
                  <Hotkey className={clsx('flex items-center self-stretch')}>
                    {HOTKEY_MAPPING.enter}
                  </Hotkey>
                )}
              </div>
            )}
          </Combobox.Option>
        </div>
      )}
    </div>
  )
}
